import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Check, MapPin } from 'lucide-react';
import { ServicePageLayout } from '../components/ServicePageLayout';
import { getCity } from '../data/cityData';
const relatedServices = [{
  label: 'Interior Detailing',
  to: '/services/interior-detailing'
}, {
  label: 'Exterior Detailing',
  to: '/services/exterior-detailing'
}, {
  label: 'Full Car Detail',
  to: '/services/full-car-detail'
}, {
  label: 'Engine Bay Detailing',
  to: '/services/engine-bay-detailing'
}, {
  label: 'Headlight Restoration',
  to: '/services/headlight-restoration'
}, {
  label: 'Mobile Detailing',
  to: '/services/mobile-detailing'
}];
export function CityPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const city = slug ? getCity(slug) : undefined;
  if (!city) {
    return <Navigate to="/" replace data-id="element-541" />;
  }
  const baseUrl = 'https://www.fulldetailpro.com';
  const path = `/ventura-county/${city.slug}`;
  const schema = {
    '@context': 'https://schema.org',
    '@type': ['AutoRepair', 'LocalBusiness'],
    name: `Mike's Pro Detail — Mobile Detailing ${city.name}`,
    image: 'https://cdn.magicpatterns.com/uploads/8ujQRT1VS2FomWSs5Jvzhk/mikesprodetail2026logo.png',
    description: city.metaDescription,
    telephone: '+18183819905',
    priceRange: '$160-$350',
    url: `${baseUrl}${path}`,
    areaServed: {
      '@type': 'City',
      name: `${city.name}, CA`
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: city.name,
      addressRegion: 'CA',
      addressCountry: 'US'
    }
  };
  const breadcrumbs = [{
    name: 'Home',
    item: `${baseUrl}/`
  }, {
    name: 'Service Areas',
    item: `${baseUrl}/#service-areas`
  }, {
    name: city.name,
    item: `${baseUrl}${path}`
  }];
  return <ServicePageLayout title={city.title} subtitle={city.subtitle} metaDescription={city.metaDescription} schema={schema} faqs={city.faqs} breadcrumbs={breadcrumbs} data-id="element-542">
      {city.paragraphs.map((p, i) => <p key={i} data-id="element-543">{p}</p>)}

      <h2 data-id="element-544">Mobile Detailing Services in {city.name}</h2>
      <p data-id="element-545">
        We bring the full range of professional auto detailing to {city.name},
        with every service performed on-site at your home or office. Popular
        options for {city.name} drivers include:
      </p>
      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-546">
        <li className="flex items-start" data-id="element-547">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-548" />
          <span data-id="element-549">
            <strong data-id="element-550">Interior Detailing:</strong> Deep vacuuming, shampooing,
            steam sanitizing, and leather conditioning for a like-new cabin.
          </span>
        </li>
        <li className="flex items-start" data-id="element-551">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-552" />
          <span data-id="element-553">
            <strong data-id="element-554">Exterior Detailing:</strong> Safe hand wash, clay bar
            decontamination, and durable wax or sealant protection.
          </span>
        </li>
        <li className="flex items-start" data-id="element-555">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-556" />
          <span data-id="element-557">
            <strong data-id="element-558">Full Car Detail:</strong> Our most popular bumper-to-bumper
            package combining interior and exterior services.
          </span>
        </li>
        <li className="flex items-start" data-id="element-559">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-560" />
          <span data-id="element-561">
            <strong data-id="element-562">Engine Bay & Headlight Restoration:</strong> Specialty
            services to keep every part of your vehicle looking its best.
          </span>
        </li>
      </ul>

      <h2 data-id="element-563">Neighborhoods We Serve in {city.name}</h2>
      <p data-id="element-564">
        Our mobile detailing service reaches every corner of {city.name},
        including {city.neighborhoods.slice(0, -1).join(', ')}, and{' '}
        {city.neighborhoods[city.neighborhoods.length - 1]}. Wherever you park,
        we come to you with our own water and power.
      </p>
      <div className="not-prose flex flex-wrap gap-3 my-6" data-id="element-565">
        {city.neighborhoods.map(n => <span key={n} className="inline-flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-gray-300" data-id="element-566">
            <MapPin className="w-4 h-4 text-red-500" data-id="element-567" />
            {n}
          </span>)}
      </div>

      <h2 data-id="element-568">Explore Our Detailing Services</h2>
      <p data-id="element-569">
        Learn more about each of our professional mobile detailing services
        available in {city.name} and throughout Ventura County:
      </p>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-570">
        {relatedServices.map(s => <Link key={s.to} to={s.to} className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-571">
            {s.label} &rarr;
          </Link>)}
      </div>
    </ServicePageLayout>;
}